import React from 'react';
import PropTypes from 'prop-types';

import { Modal } from 'react-bootstrap';

import ManagedInput from 'components/form/managed_input';
import ManagedFormGroup from 'components/form/managed_input/form_group';
import Options from 'components/form/managed_input/options';
import {
  BUY_SIDE,
  getTransactionSideLabel,
  SELL_SIDE
} from 'bundles/backoffice/vouchers/helpers/transaction_side_details';
import { commissionModes, FLAT_RATE, PERCENT_OF_RENT, RATE_PER_SPACE_SIZE_UNIT } from './commission_modes';
import { leaseRateUnitOptions } from 'components/lease_structure/form/helpers/select_options';

const defaultState = {
  buySideCommissionPercent: undefined,
  buySideCommissionRate: undefined,
  commissionPercent: undefined,
  commissionRate: undefined,
  duration: null,
  durationUnits: 'years',
  escalationFrequency: null,
  escalationFrequencyUnits: 'years',
  escalationRate: null,
  escalationRateUnits: 'percent',
  initialLeaseRate: null,
  leaseRateUnits: 'per_space_size_unit_per_year',
  operatingExpensesRate: 0,
  sellSideCommissionPercent: undefined,
  sellSideCommissionRate: undefined,
  startDate: null,
  useTransactionSideValues: false,
};

class RentEscalationModal extends React.Component {
  static propTypes = {
    buySideCommissionPercent: PropTypes.number,
    buySideCommissionRate: PropTypes.number,
    commissionMode: PropTypes.oneOf(commissionModes).isRequired,
    commissionPercent: PropTypes.number,
    commissionRate: PropTypes.number,
    duration: PropTypes.number,
    durationUnits: PropTypes.string,
    enableOperatingExpenses: PropTypes.bool,
    headerLabel: PropTypes.string,
    initialLeaseRate: PropTypes.number,
    leaseRateUnits: PropTypes.string,
    saveButtonLabel: PropTypes.string,
    sellSideCommissionPercent: PropTypes.number,
    sellSideCommissionRate: PropTypes.number,
    show: PropTypes.bool,
    spaceSizeUnits: PropTypes.string,
    transactionSideOptions: PropTypes.array,
    useTransactionSideValues: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
  }

  static defaultProps = {
    headerLabel: 'Rent Escalation',
    saveButtonLabel: 'Add Lease Terms',
    transactionSideOptions: [],
  }

  state = { ...defaultState };

  componentDidUpdate(previousProps) {
    const readProp = key => this.props[key] == null ? defaultState[key] : this.props[key];
    const useTransactionSideValues = this.props.useTransactionSideValues;

    if (!previousProps.show && this.props.show) {
      this.setState({
        buySideCommissionPercent: useTransactionSideValues ? readProp('buySideCommissionPercent') : 0,
        buySideCommissionRate: useTransactionSideValues ? readProp('buySideCommissionRate') : 0,
        commissionPercent: useTransactionSideValues ? 0 : readProp('commissionPercent'),
        commissionRate: useTransactionSideValues ? 0 : readProp('commissionRate'),
        duration: readProp('duration'),
        durationUnits: readProp('durationUnits'),
        escalationFrequency: null,
        escalationFrequencyUnits: 'years',
        escalationRate: null,
        escalationRateUnits: 'percent',
        initialLeaseRate: readProp('initialLeaseRate'),
        operatingExpensesRate: readProp('operatingExpensesRate'),
        leaseRateUnits: readProp('leaseRateUnits'),
        sellSideCommissionPercent: useTransactionSideValues ? readProp('sellSideCommissionPercent') : 0,
        sellSideCommissionRate: useTransactionSideValues ? readProp('sellSideCommissionRate') : 0,
        useTransactionSideValues: readProp('useTransactionSideValues'),
        startDate: null
      });
    }
  }

  handleChange = (changes) => {
    this.setState(changes);
  }

  handleSelectChange = (event) => {
    const { name, value } = event.target;
    this.setState({ [name]: value });
  }

  handleSubmit = () => {
    const escalation = {
      ...this.state,
      includeOperatingExpenses: this.state.operatingExpensesRate > 0,
    };

    this.props.onSubmit(escalation);
    this.props.onClose();
  }

  render() {
    return (
      <Modal
        backdrop="static"
        className="form-modal visible-overflow"
        show={this.props.show}
        onCancel={this.props.onClose}
        onHide={this.props.onClose}
      >
        <Modal.Header closeButton>
          {this.props.headerLabel}
        </Modal.Header>

        <Modal.Body>
          {this.renderForm()}
        </Modal.Body>

        <Modal.Footer>
          {this.renderControls()}
        </Modal.Footer>
      </Modal>
    );
  }

  renderControls() {
    return <React.Fragment>
      <button
        className="btn btn-sm btn-cancel"
        name="cancel"
        onClick={this.props.onClose}
      >
        Cancel
      </button>

      <button
        aria-label="save-button"
        className="btn btn-sm btn-save"
        disabled={Object.values(this.state).some(value => value === null) || this.state.escalationFrequency <= 0}
        name="submit"
        onClick={this.handleSubmit}
      >
        {this.props.saveButtonLabel}
      </button>
    </React.Fragment>;
  }

  renderForm() {
    return (
      <div className="form-horizontal rent-escalation-form">
        <div className="row">
          <div className="col-sm-12">
            <h3 className="mt-0">Term</h3>
          </div>

          <div className="col-sm-12">
            <div className="form-group">
              <label className="control-label">Start Date</label>
              <div className="form-input-container">
                <ManagedInput
                  aria-label="start-date"
                  className="form-control"
                  name="startDate"
                  type="date"
                  value={this.state.startDate}
                  onChange={this.handleChange}
                />
              </div>
            </div>
          </div>

          <div className="col-sm-8">
            <div className="form-group">
              <label className="control-label">Duration</label>
              <div className="form-input-container">
                <ManagedInput
                  aria-label="duration"
                  className="form-control"
                  name="duration"
                  type="number"
                  value={this.state.duration}
                  onChange={this.handleChange}
                />
              </div>
            </div>
          </div>

          <div className="inline-input col-sm-4">
            <select
              aria-label="duration-units"
              className="form-control rent-escalation-select"
              name="durationUnits"
              value={this.state.durationUnits}
              onChange={this.handleSelectChange}
            >
              <option value="years">Years</option>
              <option value="months">Months</option>
            </select>
          </div>

          {this.props.enableOperatingExpenses &&
            <React.Fragment>
              <div className="col-sm-12">
                <h3>Operating Expenses</h3>
              </div>
              <div className="col-sm-8">
                <ManagedFormGroup
                  label="Operating Expenses Rate"
                  name="operatingExpensesRate"
                  type="currency"
                  value={this.state.operatingExpensesRate}
                  onChange={this.handleChange}
                />
              </div>
            </React.Fragment>
          }

          <div className="col-sm-12">
            <h3>Escalation</h3>
          </div>

          <div className="col-sm-8">
            <div className="form-group">
              <label className="control-label">
                Initial {this.props.enableOperatingExpenses && 'Base'} Lease Rate
              </label>
              <div className="form-input-container">
                <ManagedInput
                  aria-label="initial-lease-rate"
                  className="form-control"
                  name="initialLeaseRate"
                  type="currency"
                  value={this.state.initialLeaseRate}
                  onChange={this.handleChange}
                />
              </div>
            </div>
          </div>

          <div className="inline-input col-sm-4">
            <select
              aria-label="lease-rate-units"
              className="form-control rent-escalation-select"
              name="leaseRateUnits"
              value={this.state.leaseRateUnits}
              onChange={this.handleSelectChange}
            >
              <Options options={leaseRateUnitOptions({ spaceSizeUnits: this.props.spaceSizeUnits || 'sf' })} />
            </select>
          </div>

          <div className="col-sm-8">
            <div className="form-group">
              <label className="control-label">Escalation Amount</label>
              <div className="form-input-container">
                <ManagedInput
                  aria-label="escalation-amount"
                  className="form-control"
                  name="escalationRate"
                  type={this.state.escalationRateUnits === 'percent' ? 'number' : 'currency'}
                  value={this.state.escalationRate}
                  onChange={this.handleChange}
                />
              </div>
            </div>
          </div>

          <div className="inline-input col-sm-4">
            <select
              aria-label="escalation-rate-units"
              className="form-control rent-escalation-select"
              name="escalationRateUnits"
              value={this.state.escalationRateUnits}
              onChange={this.handleSelectChange}
            >
              <option value="percent">Percent</option>
              <option value="dollars">Dollars</option>
            </select>
          </div>

          <div className="col-sm-8">
            <div className="form-group">
              <label className="control-label">Escalate Every</label>
              <div className="form-input-container">
                <ManagedInput
                  aria-label="escalation-frequency"
                  className="form-control"
                  name="escalationFrequency"
                  type="number"
                  value={this.state.escalationFrequency}
                  onChange={this.handleChange}
                />
              </div>
            </div>
          </div>

          <div className="inline-input col-sm-4">
            <select
              className="form-control rent-escalation-select"
              name="escalationFrequencyUnits"
              value={this.state.escalationFrequencyUnits}
              onChange={this.handleSelectChange}
            >
              <option value="years">Years</option>
              <option value="months">Months</option>
            </select>
          </div>

          {this.state.useTransactionSideValues
            ? this.renderTransactionSideCommissionAmounts()
            : this.renderCommissionAmount()}
        </div>
      </div>
    );
  }

  renderCommissionAmount() {
    if ([RATE_PER_SPACE_SIZE_UNIT, FLAT_RATE].includes(this.props.commissionMode)) {
      return;
    }

    const isPercent = this.props.commissionMode === PERCENT_OF_RENT;
    return (
      <div className="col-sm-8">
        <div className="form-group">
          <label className="control-label">Commission {isPercent ? 'Percent' : 'Rate'}</label>
          <div className="form-input-container">
            <ManagedInput
              aria-label={isPercent ? 'commission-percent' : 'commission-rate'}
              className="form-control"
              name={isPercent ? 'commissionPercent' : 'commissionRate'}
              type={isPercent ? 'number' : 'currency'}
              value={isPercent ? this.state.commissionPercent : this.state.commissionRate}
              onChange={this.handleChange}
            />
          </div>
        </div>
      </div>
    );
  }

  renderTransactionSideCommissionAmounts() {
    if ([RATE_PER_SPACE_SIZE_UNIT, FLAT_RATE].includes(this.props.commissionMode)) {
      return;
    }

    const isPercent = this.props.commissionMode === PERCENT_OF_RENT;
    const sellSideLabel = getTransactionSideLabel(SELL_SIDE, this.props.transactionSideOptions);
    const buySideLabel = getTransactionSideLabel(BUY_SIDE, this.props.transactionSideOptions);
    return (
      <React.Fragment>
        <div className="col-sm-8">
          <div className="form-group">
            <label className="control-label">{sellSideLabel} Commission {isPercent ? '%' : 'Rate'}</label>
            <div className="form-input-container">
              <ManagedInput
                aria-label={isPercent ? 'sell_side-commission-percent' : 'sell_side-commission-rate'}
                className="form-control"
                name={isPercent ? 'sellSideCommissionPercent' : 'sellSideCommissionRate'}
                type={isPercent ? 'number' : 'currency'}
                value={isPercent ? this.state.sellSideCommissionPercent : this.state.sellSideCommissionRate}
                onChange={this.handleChange}
              />
            </div>
          </div>
        </div>
        <div className="col-sm-8">
          <div className="form-group">
            <label className="control-label">{buySideLabel} Commission {isPercent ? '%' : 'Rate'}</label>
            <div className="form-input-container">
              <ManagedInput
                aria-label={isPercent ? 'buy_side-commission-percent' : 'buy_side-commission-rate'}
                className="form-control"
                name={isPercent ? 'buySideCommissionPercent' : 'buySideCommissionRate'}
                type={isPercent ? 'number' : 'currency'}
                value={isPercent ? this.state.buySideCommissionPercent : this.state.buySideCommissionRate}
                onChange={this.handleChange}
              />
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }
}

export default RentEscalationModal;
